import type { DateValue } from "@internationalized/date";
import { useState } from "react";
import type { DateRangePickerProps as AriaDateRangePickerProps } from "react-aria-components";
import {
  DateRangePicker as AriaDateRangePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { RangeCalendar } from "./range-calendar";
import { RangePresetButton } from "./range-preset";

export interface DateRangePreset {
  label: string;
  value: { start: DateValue; end: DateValue };
  subtext?: string;
}

interface DateRangeCalendarPopoverProps extends Omit<AriaDateRangePickerProps<DateValue>, "children"> {
  /** The function to call when the apply button is clicked. */
  onApply?: () => void;
  /** The function to call when the cancel button is clicked. */
  onCancel?: () => void;
  /** The function to call when the reset button is clicked. */
  onReset?: () => void;
  /** Text for the reset button */
  resetText?: string;
  /** Minimum date value */
  minValue?: DateValue;
  /** Maximum date value */
  maxValue?: DateValue;
  /** Date limits for filtering available presets */
  limits?: { minDate: DateValue; maxDate: DateValue };
  /** Popover placement */
  placement?: "bottom" | "bottom right" | "bottom left" | "top" | "top right" | "top left";
  /** Whether to show the preset sidebar on desktop */
  showPresets?: boolean;
  /** Preset buttons to show in sidebar */
  presets?: Record<string, DateRangePreset>;
  /** Currently selected preset key */
  selectedPresetKey?: string | null;
  /** Callback when a preset is selected */
  onPresetSelect?: (key: string, preset: DateRangePreset) => void;
  /** The trigger button */
  children: React.ReactNode;
}

export const DateRangeCalendarPopover: React.FC<DateRangeCalendarPopoverProps> = ({
  value,
  onChange,
  onApply,
  onCancel,
  onReset,
  resetText = "Reset to latest week",
  minValue,
  maxValue,
  limits,
  placement = "bottom right",
  showPresets = false,
  presets,
  selectedPresetKey,
  onPresetSelect,
  children,
  ...props
}) => {
  const [focusedValue, setFocusedValue] = useState<DateValue | null>(null);

  // Helper to check if a date range is within limits
  const isRangeAvailable = (start: DateValue, end: DateValue): boolean => {
    if (!limits) return true;
    return start.compare(limits.minDate) >= 0 && end.compare(limits.maxDate) <= 0;
  };

  return (
    <AriaDateRangePicker
      aria-label="Date range picker"
      shouldCloseOnSelect={false}
      value={value}
      onChange={onChange}
      {...props}
    >
      <AriaGroup>{children}</AriaGroup>
      <AriaPopover
        placement={placement}
        offset={8}
        className={({ isEntering, isExiting }) =>
          cx(
            "origin-(--trigger-anchor-point) will-change-transform",
            isEntering &&
              "duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
            isExiting &&
              "duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
          )
        }
      >
        <AriaDialog className="flex rounded-2xl bg-primary shadow-none ring ring-secondary_alt focus:outline-hidden">
          {({ close }) => (
            <>
              {showPresets && presets && (
                <div className="hidden w-38 flex-col gap-0.5 border-r border-solid border-secondary p-3 lg:flex">
                  {Object.entries(presets).map(([key, preset]) => {
                    const isAvailable = isRangeAvailable(preset.value.start, preset.value.end);
                    return (
                      <RangePresetButton
                        key={preset.label}
                        value={preset.value}
                        disabled={!isAvailable}
                        subtext={preset.subtext}
                        onClick={() => {
                          if (isAvailable && onPresetSelect) {
                            onPresetSelect(key, preset);
                            setFocusedValue(preset.value.start);
                          }
                        }}
                      >
                        {preset.label}
                      </RangePresetButton>
                    );
                  })}
                </div>
              )}
              <div className="flex flex-col">
                <RangeCalendar
                  focusedValue={focusedValue}
                  onFocusChange={setFocusedValue}
                  minValue={minValue}
                  maxValue={maxValue}
                />
                <div className="flex justify-between gap-3 border-t border-secondary p-4">
                  <div className="hidden items-center gap-3 md:flex">
                    {onReset && (
                      <Button
                        size="md"
                        color="secondary"
                        onClick={() => {
                          onReset();
                          close();
                        }}
                      >
                        {resetText}
                      </Button>
                    )}
                  </div>
                  <div className="grid w-full grid-cols-2 gap-3 md:flex md:w-auto">
                    <Button
                      size="md"
                      color="secondary"
                      onClick={() => {
                        onCancel?.();
                        close();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="md"
                      color="primary"
                      onClick={() => {
                        onApply?.();
                        close();
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </AriaDialog>
      </AriaPopover>
    </AriaDateRangePicker>
  );
};
